import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { LocalizedMessage, LocalizedPrice, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Models
import mCarpark from 'models/cpm/carpark';
import mFinance from 'models/cpm/finance';
import mPriceRule from 'models/cpm/priceRule';
import mParking from 'models/cpm/parking';
// HOCS
import withLoading from 'hocs/WithLoading';
// Custom
import { cpmBookingDataShape, listRowSpecsShape } from 'constants/PropTypesShapes';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';
import { dateGenerator } from 'utils/DateUtils';
import AppIcon from 'components/icons/AppIcon';
import { CancelIconButton, SelectIconButton } from 'components/buttons/IconButtons';
import { showPath } from 'cpmMyBookings/navigation';
import MyBookingStatusInfoIcon from './MyBookingStatusInfoIcon';

/* ========================================================= */
/* SUB COMPONENTS                                            */
/* ========================================================= */

const BookingPrice = ({ booking }) => {
  const { priceRule } = mFinance.lastPurchase(booking);
  const allPurchasesTotalAmountInclTax = mFinance.purchaseSumPaidAmount(booking.purchases);

  // Free: show the corresponding icon
  if (mFinance.lastPurchasePriceIsFree(booking)) {
    const { labelId: typeLabelId, icon: typeIcon } = mFinance.lastPurchasePriceType(booking);
    return (
      <div className="d-flex align-items-center">
        <Tooltip content={<LocalizedMessage id={typeLabelId} />}>
          <AppIcon id={typeIcon} defaultColor />
        </Tooltip>
      </div>
    );
  }

  // Show price with currency
  return (
    <div className="d-flex align-items-center">
      <div className="ml-2">
        <LocalizedPrice
          currency={priceRule.currency}
          value={mPriceRule.displayAmount(allPurchasesTotalAmountInclTax, priceRule)}
        />
      </div>
    </div>
  );
};

BookingPrice.propTypes = {
  booking: cpmBookingDataShape.isRequired,
};

const MyBookingRow = React.forwardRef(
  ({
    booking,
    rowSpecs,
    timezone,
    handleStartLoading,
    onCancel,
  },
  ref) => {
    const canBeCancelled = mParking.isCreated(booking);

    // Handlers
    const handleCancel = () => {
      onCancel(booking);
    };

    return (
      <TableRow
        ref={ref}
        element={booking}
        rowSpecs={rowSpecs}
        actions={[
          canBeCancelled && <CancelIconButton key="bCancel" onClick={handleCancel} />,
          <SelectIconButton key="bDisplay" onClick={handleStartLoading} path={showPath(booking.reference)} />,
        ]}
      >
        {/* ----- Status ----- */}
        <TableCell
          className={classnames('table-content-1 table-content-center', {
            'table-first-details': rowSpecs.withDetails,
          })}
        >
          <MyBookingStatusInfoIcon booking={booking} />
        </TableCell>
        {/* ----- Carpark name ----- */}
        <TableCell className="hidden-md-down">
          <TruncatedTooltipText>{mCarpark.publicName(booking.carpark)}</TruncatedTooltipText>
        </TableCell>
        {/* ----- Reference ----- */}
        <TableCell>{booking.reference}</TableCell>
        {/* ----- Begin Date ----- */}
        <TableCell className="hidden-sm-down">
          {booking.begin && (
            <TruncatedTooltipText>
              <LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(booking.begin, timezone)} />
            </TruncatedTooltipText>
          )}
        </TableCell>
        {/* ----- End Date ----- */}
        <TableCell className="hidden-sm-down">
          {booking.end && (
            <TruncatedTooltipText>
              <LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(booking.end, timezone)} />
            </TruncatedTooltipText>
          )}
        </TableCell>
        {/* ----- Price ----- */}
        <TableCell className="hidden-md-down">
          <BookingPrice booking={booking} />
        </TableCell>
      </TableRow>
    );
  },
);

MyBookingRow.displayName = 'MyBookingRow';

MyBookingRow.propTypes = {
  booking: cpmBookingDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  timezone: PropTypes.string.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

module.exports = withLoading(MyBookingRow);



// WEBPACK FOOTER //
// ./routes/my_bookings/assets/MyBookingRow.js